import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { drawsFrom, numbersOf, prettyGame } from "../utils/draws";

/**
 * How often each digit 0-9 has appeared in recent draws, against the frequency
 * you would expect if the draw is fair.
 *
 * This previously plotted ten hardcoded numbers labelled "Example". Real data
 * is the point: the interesting result is that the bars sit close to the
 * expected line, which is the honest answer to "are some numbers hot?".
 *
 * One measure, one axis, one series. The dashed expected line is a reference
 * rather than a second series, so it takes a different hue and is named
 * directly in the legend.
 */

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

/** Read a design token so the chart follows the theme instead of hardcoding hex. */
const token = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

function countDigits(draws) {
  const counts = new Array(10).fill(0);
  let total = 0;

  draws.forEach((draw) => {
    numbersOf(draw).forEach((value) => {
      const digit = Number(value);
      if (Number.isInteger(digit) && digit >= 0 && digit <= 9) {
        counts[digit] += 1;
        total += 1;
      }
    });
  });

  return { counts, total };
}

const AnalyticsChart = ({ game = "Daily3" }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [state, setState] = useState({ status: "loading", counts: [], total: 0, draws: 0 });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`/api/draw?game=${game}`);
        if (!res.ok) throw new Error(`Draw API returned ${res.status}`);
        const body = await res.json();
        const draws = drawsFrom(body);
        if (cancelled) return;

        const { counts, total } = countDigits(draws);
        setState(
          total > 0
            ? { status: "ready", counts, total, draws: draws.length }
            : { status: "empty", counts: [], total: 0, draws: 0 },
        );
      } catch {
        if (!cancelled) setState({ status: "error", counts: [], total: 0, draws: 0 });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [game]);

  const expected = useMemo(() => (state.total ? state.total / 10 : 0), [state.total]);

  useEffect(() => {
    if (state.status !== "ready" || !canvasRef.current) return undefined;

    const series1 = token("--series-1", "#2a78d6");
    const series2 = token("--series-2", "#eb6834");
    const ink1 = token("--ink-1", "#0b0b0b");
    const ink2 = token("--ink-2", "#52514e");
    const ink3 = token("--ink-3", "#83827c");
    const line = token("--line", "rgba(11,11,11,0.1)");
    const surface = token("--surface-1", "#fcfcfb");

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: DIGITS,
        datasets: [
          {
            label: "Times drawn",
            data: state.counts,
            backgroundColor: series1,
            // 4px rounded data-end, square where it meets the baseline
            borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
            borderSkipped: "bottom",
            // a sliver of surface between adjacent bars
            borderColor: surface,
            borderWidth: { top: 0, right: 1, bottom: 0, left: 1 },
            categoryPercentage: 0.62,
            barPercentage: 0.86,
          },
          {
            label: "Expected if fair",
            type: "line",
            data: new Array(10).fill(expected),
            borderColor: series2,
            borderWidth: 2,
            borderDash: [5, 4],
            pointRadius: 0,
            pointHitRadius: 0,
            fill: false,
            order: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          // Identity comes from the HTML legend below, which survives print
          // and forced-colors better than a canvas-drawn one.
          legend: { display: false },
          tooltip: {
            backgroundColor: ink1,
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (items) => `Digit ${items[0].label}`,
              label: (item) =>
                item.datasetIndex === 0
                  ? `Drawn ${item.raw} times`
                  : `Expected ${Number(item.raw).toFixed(1)} if fair`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { color: line },
            ticks: { color: ink2, font: { size: 12 } },
          },
          y: {
            beginAtZero: true,
            grid: { color: line, drawTicks: false },
            border: { display: false },
            ticks: { color: ink3, font: { size: 12 }, padding: 8, precision: 0 },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [state, expected]);

  if (state.status === "loading") return <p className="chart-sub">Loading recent draws…</p>;
  if (state.status === "error") return <p className="error">Could not load draw results just now.</p>;
  if (state.status === "empty") return <p className="chart-sub">No draw results available yet.</p>;

  const spread = Math.max(...state.counts) - Math.min(...state.counts);

  return (
    <div className="chart-card">
      <div className="chart-head">
        <h3>Digit frequency — {prettyGame(game)}</h3>
        <p className="chart-sub">
          Across the last {state.draws} draws ({state.total} digits). The most and least
          common digits differ by {spread} — the kind of gap chance alone produces at this
          sample size, not a pattern to bet on.
        </p>
      </div>

      <div className="chart-frame">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Bar chart of how often each digit 0 to 9 was drawn in ${prettyGame(game)}`}
        />
      </div>

      <div className="chart-legend">
        <span><i className="swatch" aria-hidden="true" /> Times drawn</span>
        <span>
          <i className="swatch swatch--line" aria-hidden="true" /> Expected if fair ({expected.toFixed(1)})
        </span>
      </div>

      <details className="table-view">
        <summary>View as table</summary>
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Digit</th>
              <th scope="col">Times drawn</th>
              <th scope="col">Expected</th>
            </tr>
          </thead>
          <tbody>
            {state.counts.map((count, digit) => (
              <tr key={digit}>
                <td>{digit}</td>
                <td>{count}</td>
                <td>{expected.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};

export default AnalyticsChart;
